import React, { useEffect, useState } from 'react';
import { useMoralisCloudFunction } from 'react-moralis';
import { ethers } from 'ethers';
import { ITransaction } from '../../components/TransactionsList/transactionsList';
import { IGame } from '../Games/useGames';
import { emptyUser, IUser } from '../User/useUser';

interface ISearchResults {
	users: IUser[];
	games: IGame[];
	transactions: ITransaction[];
}

export const emptySearchResults = {
	users: [],
	games: [],
	transactions: [],
};

export interface ISearchProvider {
	data: ISearchResults;
	loadData: (id: string | null) => void;
	isLoading: boolean;
}

export const emptySearchContext = {
	data: { users: [], games: [], transactions: [] },
	loadData: () => null as unknown as void,
	isLoading: true,
};

const isProfile = (address: string) => {
	return !(address.length !== 42 || !ethers.utils.isAddress(address.toLowerCase()));
};

export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
	const [data, setData] = useState<ISearchResults>(emptySearchResults);
	const [dataId, setDataId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const {
		data: queryData,
		fetch: queryFetch,
		isFetching: queryIsFetching,
		isLoading: queryIsLoading,
	} = useMoralisCloudFunction('findByTextPhrase', {}, { autoFetch: false });

	const loadData = (id: string | null) => {
		setIsLoading(true);
		setDataId(id);

		if (!id) {
			setIsLoading(false);
		}
	};

	/** Fetching effect */
	useEffect(() => {
		if (!dataId || queryIsLoading) {
			setData(emptySearchResults);

			return;
		}

		(async () => {
			const payload = (await queryFetch({ params: { searchPhrase: dataId } })) as {
				[p: string]: ({ attributes: IUser | IGame | ITransaction } & unknown)[];
			};

			const unregisteredGameProfileData: IUser = {
				...emptyUser,
				address: dataId,
			};

			const gamerProfilesData = payload.gamerProfiles.map((row: any) => row.attributes);
			const gamesData = payload.games.map((row: any) => row.attributes);
			const transactionsData = payload.transactions.map((row: any) => {
				const converted: ITransaction = {
					id: row.attributes.transaction_hash,
					value: row.attributes.amount,
					marketplace: row.attributes.marketplace ?? '0x714ef5c429ce9bdd0cac3631d30474bd04e954dc',
					token: row.attributes.license,
					user: row.attributes.user ?? row.attributes.referrer /** `referrer` is a V2 Fallback */,
					time: row.attributes.block_timestamp,
				};
				return converted;
			});

			const isGamerProfileUnregistered =
				isProfile(dataId) && gamerProfilesData.length === 0 && gamesData.length === 0;

			setData({
				users: isGamerProfileUnregistered ? [unregisteredGameProfileData] : gamerProfilesData,
				games: gamesData,
				transactions: transactionsData,
			});
		})();
	}, [dataId]);

	useEffect(
		() => setIsLoading(queryIsFetching || queryIsLoading),
		[queryIsLoading, queryIsFetching, queryData, dataId]
	);

	return (
		<SearchContext.Provider
			value={{
				data,
				isLoading,
				loadData,
			}}
		>
			{children}
		</SearchContext.Provider>
	);
};

const SearchContext = React.createContext<ISearchProvider>(emptySearchContext);

export const useSearchProvider = () => {
	return React.useContext(SearchContext);
};
